import {
  clearSessionAndRedirectToLogin,
  isAuthWebSocketClose,
  refreshAccessToken,
} from "auth/tokenRefresh";

export const getCsrfToken = (): string | null => {
  const cookies = document.cookie.split(";");
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split("=");
    if (name === "csrftoken") {
      return value;
    }
  }
  return null;
};

export function toCamelCase(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(toCamelCase);
  } else if (obj && typeof obj === "object") {
    return Object.keys(obj).reduce((acc, key) => {
      const camelKey = key.replace(/_([a-z])/g, (_, char) =>
        char.toUpperCase(),
      );
      acc[camelKey] = toCamelCase(obj[key]);
      return acc;
    }, {} as any);
  }
  return obj;
}

export const isPresignedUrl = (url: string): boolean => {
  return (
    url.includes("?") &&
    (url.includes("AWSAccessKeyId") || url.includes("X-Amz"))
  );
};

const PRESIGNED_URL_REFRESH_BUFFER_MS = 60_000;

const parseAmzDate = (amzDate: string): number | null => {
  const match = amzDate.match(
    /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/,
  );
  if (!match) {
    return null;
  }
  const [, year, month, day, hour, minute, second] = match;
  return Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second),
  );
};

export const isPresignedUrlExpired = (url: string): boolean => {
  if (!isPresignedUrl(url)) {
    return false;
  }

  try {
    const params = new URL(url).searchParams;
    const amzDate = params.get("X-Amz-Date");
    const amzExpires = params.get("X-Amz-Expires");
    if (amzDate && amzExpires) {
      const issuedAt = parseAmzDate(amzDate);
      const expiresInSeconds = Number(amzExpires);
      if (issuedAt !== null && Number.isFinite(expiresInSeconds)) {
        const expiresAt = issuedAt + expiresInSeconds * 1000;
        return Date.now() >= expiresAt - PRESIGNED_URL_REFRESH_BUFFER_MS;
      }
    }

    const legacyExpires = params.get("Expires");
    if (legacyExpires) {
      const expiresAt = Number(legacyExpires) * 1000;
      if (Number.isFinite(expiresAt)) {
        return Date.now() >= expiresAt - PRESIGNED_URL_REFRESH_BUFFER_MS;
      }
    }
  } catch {
    return false;
  }

  return false;
};

export class FetchWithCredentialsError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(`${status}: ${message}`);
    this.name = "FetchWithCredentialsError";
    this.status = status;
  }
}

const fetchWithCredentials = async (
  url: string,
  options: RequestInit = {},
): Promise<any> => {
  const csrfToken = getCsrfToken();
  let token = localStorage.getItem("accessToken");

  const headers = {
    ...(csrfToken ? { "X-CSRFToken": csrfToken } : {}),
    ...(!url.endsWith("/upload") ? { "Content-Type": "application/json" } : {}),
    ...options.headers,
    Authorization: `Bearer ${token}`,
  };

  let baseUrl = process.env.REACT_APP_SERVER_URL || "";
  if (baseUrl.endsWith("/")) {
    baseUrl = baseUrl.slice(0, -1);
  }

  const makeRequest = async (): Promise<Response> => {
    return fetch(`${baseUrl}${url}`, {
      credentials: "include",
      ...options,
      headers,
    });
  };

  let response = await makeRequest();

  if (response.status === 401) {
    const refreshResult = await refreshAccessToken();
    if (refreshResult.status === "refreshed") {
      // Try again with new access token
      token = localStorage.getItem("accessToken")!;
      headers.Authorization = `Bearer ${token}`;
      response = await makeRequest();
    } else if (refreshResult.status === "invalid") {
      // Refresh token is gone or rejected — force re-login.
      clearSessionAndRedirectToLogin();
      throw new FetchWithCredentialsError(401, "Session expired");
    } else {
      // Server/network blip: keep tokens and surface a retryable error.
      throw new FetchWithCredentialsError(
        503,
        "Session refresh temporarily unavailable",
      );
    }
  }

  if (!response.ok) {
    let errorMessage = "An unexpected error occurred";
    let errorData: any = null;

    try {
      errorData = await response.json();
      if (typeof errorData === "object" && errorData !== null) {
        if (typeof errorData.error === "string" && errorData.error.trim()) {
          errorMessage = errorData.error;
        } else if (
          typeof errorData.detail === "string" &&
          errorData.detail.trim()
        ) {
          errorMessage = errorData.detail;
        } else {
          for (const value of Object.values(errorData)) {
            if (typeof value === "string" && value.trim()) {
              errorMessage = value;
              break;
            }
            if (Array.isArray(value) && value.length > 0) {
              const first = value[0];
              if (typeof first === "string" && first.trim()) {
                errorMessage = first;
                break;
              }
            }
          }
        }
      }
      // 403 with TERMS_NOT_ACCEPTED: notify app to show terms modal
      if (
        response.status === 403 &&
        typeof errorData === "object" &&
        errorData?.detail?.code === "TERMS_NOT_ACCEPTED" &&
        errorData?.detail?.current_document
      ) {
        const currentDocument = toCamelCase(errorData.detail.current_document);
        window.dispatchEvent(
          new CustomEvent("terms-required", { detail: currentDocument }),
        );
      }
    } catch {
      try {
        const text = await response.text();
        if (text) errorMessage = text;
      } catch {
        // Silent fail — keep default error message
      }
    }

    console.error("Error response:", errorMessage);
    throw new FetchWithCredentialsError(response.status, errorMessage);
  }

  const contentType = response.headers.get("Content-Type");
  if (contentType?.includes("application/json")) {
    const json = await response.json();
    return toCamelCase(json);
  }

  return null;
};

export const regenerateFileUrl = async (
  oldUrl: string,
): Promise<string | null> => {
  try {
    const data = await fetchWithCredentials("/api/regenerate-file-url/", {
      method: "POST",
      body: JSON.stringify({ url: oldUrl }),
    });
    return data?.url || null;
  } catch (err) {
    console.error("[regenerateFileUrl] Error regenerating URL:", err);
    return null;
  }
};

export const normalizeFileUrls = (fileUrls: unknown): string[] => {
  if (!fileUrls) {
    return [];
  }
  if (Array.isArray(fileUrls)) {
    return fileUrls.filter((url): url is string => typeof url === "string" && !!url.trim());
  }
  if (typeof fileUrls === "string" && fileUrls.trim()) {
    return [fileUrls];
  }
  return [];
};

export const refreshPresignedFileUrl = async (url: string): Promise<string> => {
  if (!isPresignedUrl(url) || !isPresignedUrlExpired(url)) {
    return url;
  }
  const refreshed = await regenerateFileUrl(url);
  return refreshed || url;
};

export const uploadFile = async (file: File) => {
  const prepare = await fetchWithCredentials("/api/upload/prepare/", {
    method: "POST",
    body: JSON.stringify({
      fileName: file.name,
      contentType: file.type || "application/octet-stream",
      fileSize: file.size,
    }),
  });

  if (!prepare || prepare.mode === "multipart") {
    const formData = new FormData();
    formData.append("file", file);
    const data = await fetchWithCredentials("/api/upload", {
      method: "POST",
      body: formData,
    });
    return data.url;
  }

  // fetchWithCredentials runs toCamelCase; keep snake_case fallbacks too.
  const uploadUrl = prepare.uploadUrl || prepare.upload_url;
  const fileKey = prepare.fileKey || prepare.file_key;
  if (!uploadUrl || !fileKey) {
    throw new Error("Direct upload prepare response missing upload URL or key");
  }

  const formData = new FormData();
  const fields = prepare.fields || {};
  Object.entries(fields).forEach(([key, value]) => {
    formData.append(key, String(value));
  });
  // S3 requires the file field to be last in the multipart body.
  formData.append("file", file);

  const uploadResponse = await fetch(uploadUrl, {
    method: "POST",
    body: formData,
  });
  if (!uploadResponse.ok) {
    throw new Error(`Direct upload failed (${uploadResponse.status})`);
  }

  const completed = await fetchWithCredentials("/api/upload/complete/", {
    method: "POST",
    body: JSON.stringify({
      fileKey,
      fileName: file.name,
    }),
  });
  return completed.url;
};

export const connectToWebSocket = (
  url: string,
  onMessage: (event: MessageEvent) => void,
  onError?: (error: Event) => void,
  onClose?: (event: CloseEvent) => void,
) => {
  let socket: WebSocket | null = null;
  let reconnectAttempts = 0;
  const maxReconnectAttempts = 5;
  const reconnectDelay = 1000; // Start with 1 second
  let tokenRefreshInterval: ReturnType<typeof setInterval> | null = null;
  let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  let closedManually = false;

  const clearReconnectTimeout = () => {
    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout);
      reconnectTimeout = null;
    }
  };

  const scheduleReconnect = () => {
    if (closedManually || reconnectAttempts >= maxReconnectAttempts) {
      return;
    }
    reconnectAttempts++;
    const delay = reconnectDelay * Math.pow(2, reconnectAttempts - 1);
    clearReconnectTimeout();
    reconnectTimeout = setTimeout(() => {
      reconnectTimeout = null;
      if (closedManually || reconnectAttempts > maxReconnectAttempts) {
        return;
      }
      setupSocket();
    }, delay);
  };

  const connect = async (): Promise<WebSocket> => {
    // Prefer a fresh access token, but keep the existing one on transient failures.
    let token = localStorage.getItem("accessToken");
    const refreshResult = await refreshAccessToken();

    if (refreshResult.status === "refreshed") {
      token = localStorage.getItem("accessToken");
    } else if (refreshResult.status === "invalid") {
      clearSessionAndRedirectToLogin();
      throw new Error("Invalid session");
    } else if (!token) {
      // Transient failure and no access token to fall back on — retry later.
      throw new Error("Temporary token unavailable");
    }

    const baseUrl = process.env.REACT_APP_SERVER_URL || "ws://localhost:8000";
    const wsBaseUrl = baseUrl.replace(/^http/, "ws");
    const fullUrl = `${wsBaseUrl}${url}?token=${token}`;

    return new Promise((resolve, reject) => {
      const ws = new WebSocket(fullUrl);
      let settled = false;

      const connectionTimeout = setTimeout(() => {
        if (ws.readyState === WebSocket.CONNECTING) {
          ws.close();
          if (!settled) {
            settled = true;
            reject(new Error("WebSocket connection timeout"));
          }
        }
      }, 10000); // 10 second timeout

      ws.onopen = () => {
        clearTimeout(connectionTimeout);
        if (settled) {
          return;
        }
        settled = true;
        reconnectAttempts = 0; // Reset reconnect attempts on successful connection
        resolve(ws);
      };

      ws.onerror = (error) => {
        clearTimeout(connectionTimeout);
        if (settled) {
          return;
        }
        settled = true;
        reject(error);
      };
    });
  };

  const setupSocket = async () => {
    if (closedManually) {
      return;
    }

    try {
      socket = await connect();
      if (closedManually) {
        socket.close(1000, "Manual close");
        return;
      }

      socket.onmessage = onMessage;

      socket.onerror = (error) => {
        console.error("WebSocket error:", error);
        onError?.(error);
      };

      // Set up periodic token refresh to prevent expiration
      tokenRefreshInterval = setInterval(
        async () => {
          if (socket && socket.readyState === WebSocket.OPEN) {
            const refreshResult = await refreshAccessToken();
            // Only force login when the refresh token itself is rejected.
            if (refreshResult.status === "invalid") {
              clearSessionAndRedirectToLogin();
            }
          }
        },
        5 * 60 * 1000,
      ); // Refresh every 5 minutes

      socket.onclose = async (event) => {
        if (tokenRefreshInterval) {
          clearInterval(tokenRefreshInterval);
          tokenRefreshInterval = null;
        }

        onClose?.(event);

        // Don't reconnect on intentional/manual close.
        if (closedManually || event.code === 1000) {
          return;
        }

        if (reconnectAttempts >= maxReconnectAttempts) {
          return;
        }

        // Auth-looking closes: refresh first. Logout only if refresh is invalid.
        // Abnormal closes (e.g. 1006 from a busy proxy) are not auth failures —
        // reconnect with backoff and keep the session.
        if (isAuthWebSocketClose(event)) {
          const refreshResult = await refreshAccessToken();
          if (refreshResult.status === "invalid") {
            clearSessionAndRedirectToLogin();
            return;
          }
        }

        scheduleReconnect();
      };
    } catch (error) {
      console.error("WebSocket setup failed:", error);
      if (closedManually) {
        return;
      }
      // Invalid refresh already redirected — do not retry.
      if (error instanceof Error && error.message === "Invalid session") {
        return;
      }
      // Transient connect failures (busy refresh / timeout): retry without logout.
      scheduleReconnect();
    }
  };

  // Start the connection
  setupSocket();

  // Return a socket-like object with a close method
  const socketWrapper = {
    close: () => {
      closedManually = true;
      clearReconnectTimeout();
      if (tokenRefreshInterval) {
        clearInterval(tokenRefreshInterval);
        tokenRefreshInterval = null;
      }
      if (socket) {
        socket.close(1000, "Manual close");
      }
    },
    send: (data: Parameters<WebSocket["send"]>[0]) => {
      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.send(data);
      }
    },
    readyState: WebSocket.CONNECTING as number,
  };

  // Update readyState when socket is available
  const updateReadyState = () => {
    if (socket) {
      socketWrapper.readyState = socket.readyState;
    }
  };

  // Set up an interval to update readyState
  const interval = setInterval(updateReadyState, 100);

  // Override close to also clear the interval
  const originalClose = socketWrapper.close;
  socketWrapper.close = () => {
    clearInterval(interval);
    originalClose();
  };

  return socketWrapper;
};

export default fetchWithCredentials;
